import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { CATEGORIES, CATEGORY_LABELS, formatDate } from "@/lib/format";
import { Plus, Search, Copy, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

function statusLabel(a) {
  if (a.status === "draft") return { text: "Entwurf", cls: "bg-gray-200 text-gray-700" };
  if (a.status === "archived") return { text: "Archiviert", cls: "bg-charcoal/10 text-charcoal" };
  if (a.status === "published" && a.published_at && new Date(a.published_at) > new Date())
    return { text: "Programmiert", cls: "bg-gold/20 text-gold-hover" };
  return { text: "Veröffentlicht", cls: "bg-green-100 text-green-700" };
}

export default function Articles() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    api.get("/admin/articles", { params: { search: search || undefined, category: category || undefined, status: status || undefined, page, limit: 20 } })
      .then((r) => setData(r.data));
  }, [search, category, status, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, category, status]);

  const duplicate = async (id) => {
    await api.post(`/admin/articles/${id}/duplicate`);
    toast.success("Artikel dupliziert."); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Diesen Artikel wirklich löschen?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Artikel gelöscht."); load();
  };

  return (
    <AdminLayout title="Artikel">
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="article-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Titel suchen…"
              className="border border-edborder bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-prussian w-56" />
          </div>
          <select data-testid="article-filter-category" value={category} onChange={(e) => setCategory(e.target.value)}
            className="border border-edborder bg-white px-3 py-2 text-sm outline-none focus:border-prussian">
            <option value="">Alle Rubriken</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
          </select>
          <select data-testid="article-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="border border-edborder bg-white px-3 py-2 text-sm outline-none focus:border-prussian">
            <option value="">Alle Status</option>
            <option value="published">Veröffentlicht</option>
            <option value="draft">Entwurf</option>
            <option value="archived">Archiviert</option>
          </select>
        </div>
        <Link to="/admin/artikel/neu" data-testid="admin-new-article-btn"
          className="inline-flex items-center gap-2 bg-gold text-prussian-dark font-body font-bold text-sm uppercase tracking-wide px-5 py-2.5 hover:bg-gold-hover whitespace-nowrap">
          <Plus size={16} /> Neuer Artikel
        </Link>
      </div>

      <div className="bg-white border border-edborder overflow-x-auto">
        <table data-testid="admin-articles-table" className="w-full text-sm font-body">
          <thead>
            <tr className="bg-prussian text-white text-left uppercase text-[11px] tracking-wide">
              <th className="px-4 py-3">Titel</th>
              <th className="px-4 py-3">Rubrik</th>
              <th className="px-4 py-3">Autor</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Datum</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-edborder">
            {data.items.map((a) => {
              const s = statusLabel(a);
              return (
                <tr key={a.id} data-testid={`article-row-${a.id}`} className="hover:bg-offwhite">
                  <td className="px-4 py-3 max-w-sm">
                    <Link to={`/admin/artikel/${a.id}`} className="font-semibold text-prussian hover:text-gold-hover line-clamp-2">{a.title}</Link>
                  </td>
                  <td className="px-4 py-3 text-charcoal">{CATEGORY_LABELS[a.category]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.author}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] font-semibold px-2 py-1 uppercase tracking-wide ${s.cls}`}>{s.text}</span></td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{a.published_at ? formatDate(a.published_at) : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/artikel/${a.id}`} data-testid={`edit-${a.id}`} title="Bearbeiten" className="w-8 h-8 flex items-center justify-center text-prussian hover:bg-offwhite border border-edborder"><Pencil size={14} /></Link>
                      <button onClick={() => duplicate(a.id)} data-testid={`duplicate-${a.id}`} title="Duplizieren" className="w-8 h-8 flex items-center justify-center text-prussian hover:bg-offwhite border border-edborder"><Copy size={14} /></button>
                      <button onClick={() => remove(a.id)} data-testid={`delete-${a.id}`} title="Löschen" className="w-8 h-8 flex items-center justify-center text-breaking hover:bg-breaking/10 border border-edborder"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-12">Keine Artikel gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)} data-testid={`page-${p}`}
              className={`w-9 h-9 text-sm font-body border ${p === data.page ? "bg-prussian text-white border-prussian" : "bg-white border-edborder text-prussian hover:border-prussian"}`}>{p}</button>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
